(ns common.pipeline-status-actions
  "The status actions of the four pipeline entity types: deals, fundraising
   opportunities, sales opportunities and exit opportunities. The rules are the
   rules of the toolbar of the viewer of each type, so that the menu of a card
   and the viewer offer the same actions.

   A deal can be passed, put on hold, reactivated and moved to the portfolio. The
   other three types can be won, lost, put on hold and reactivated. The rules are
   data, one table for each type, so that a change to the mechanism reaches all
   four types.

   A status also has a tone, which tells a card how to look. A passed deal and a
   lost opportunity are negative. A status on hold needs caution."
  (:require
   [common.opensearch.row :as os-row]
   [common.opensearch.utils :as os-utils]))

(defn- opportunity-rules
  "The rules of a type that can be won and lost. Won, lost and put on hold are
   offered unless the opportunity already has that status. Reactivate is offered
   only for an opportunity that is on hold or lost."
  [{:keys [status-attribute active on-hold won lost]}]
  {:status-attribute status-attribute
   :statuses         #{active on-hold won lost}
   :tones            {lost    :negative
                      on-hold :caution}
   :actions          [{:id :won :unless #{won}}
                      {:id :lost :unless #{lost}}
                      {:id :put-on-hold :unless #{on-hold}}
                      {:id :reactivate :only-when #{on-hold lost}}]})

(def ^:private rules-by-entity-type
  {:entity.type/oppo
   {:status-attribute :oppo/status
    :statuses         #{:oppo.status/ok
                        :oppo.status/passed
                        :oppo.status/on-hold
                        :oppo.status/portfolio}
    :tones            {:oppo.status/passed  :negative
                       :oppo.status/on-hold :caution}
    :actions          [{:id :pass :unless #{:oppo.status/passed}}
                       {:id :put-on-hold :unless #{:oppo.status/on-hold}}
                       {:id :reactivate :only-when #{:oppo.status/on-hold
                                                     :oppo.status/passed}}
                       {:id :portfolio :unless #{:oppo.status/portfolio
                                                 :oppo.status/passed}}]}

   :entity.type/fundraising-opportunity
   (opportunity-rules {:status-attribute :fundraising-opportunity/status
                       :active           :fundraising-opportunity.status/active
                       :on-hold          :fundraising-opportunity.status/on-hold
                       :won              :fundraising-opportunity.status/won
                       :lost             :fundraising-opportunity.status/lost})

   :entity.type/sales-opportunity
   (opportunity-rules {:status-attribute :sales-opportunity/status
                       :active           :sales-opportunity.status/active
                       :on-hold          :sales-opportunity.status/on-hold
                       :won              :sales-opportunity.status/won
                       :lost             :sales-opportunity.status/lost})

   :entity.type/exit-opportunity
   (opportunity-rules {:status-attribute :exit-opportunity/status
                       :active           :exit-opportunity.status/active
                       :on-hold          :exit-opportunity.status/on-hold
                       :won              :exit-opportunity.status/won
                       :lost             :exit-opportunity.status/lost})})

(def entity-types
  "The pipeline entity types that have status actions."
  (set (keys rules-by-entity-type)))

(defn action-ids
  "Return the ids of all the actions of a pipeline entity type, in menu order.
   Return an empty vector for another type. A handler must exist for each id."
  [entity-type]
  (mapv :id (get-in rules-by-entity-type [entity-type :actions])))

(defn status-attribute
  "Return the status attribute of a pipeline entity type, for example
   :oppo/status, or nil for another type."
  [entity-type]
  (get-in rules-by-entity-type [entity-type :status-attribute]))

(defn parse-status
  "Return the status keyword from the value in a search document. The document
   holds a keyword or its string, for example \"oppo.status/ok\". Return nil for
   any other value."
  [value]
  (cond
    (keyword? value) value
    (and (string? value)
         (seq value)) (keyword value)
    :else nil))

(defn status-tone
  "Return the tone of a status of this type: :negative for a passed deal or a
   lost opportunity, :caution for a status on hold, and nil for any other status.
   Return nil for an unknown type."
  [entity-type status]
  (get-in rules-by-entity-type [entity-type :tones status]))

(defn row-status
  "Return the status of the entity of a search row, or nil when the row does not
   hold it. The row holds the value in the flat field of the status attribute of
   the type, for example `flat.oppo-status`."
  [entity-type row]
  (when-let [attribute (status-attribute entity-type)]
    (parse-status (os-row/flat-value row (os-utils/field-path->alias [attribute])))))

(defn- offered?
  [status {:keys [unless only-when]}]
  (if only-when
    (contains? only-when status)
    (not (contains? unless status))))

(defn available-actions
  "Return the ids of the actions that an entity of this type offers in this
   status, in menu order. Return an empty vector for an unknown type and for a
   status that the type does not have, because no rule applies. The status of
   another type is not a status of this type."
  [entity-type status]
  (let [{:keys [statuses actions]} (get rules-by-entity-type entity-type)]
    (if (contains? statuses status)
      (->> actions
           (filter (partial offered? status))
           (mapv :id))
      [])))
